import React, { useEffect, useState, useMemo } from "react";
import {
  FaArrowLeft,
  FaTimes,
  FaFileAlt,
  FaFileAudio,
  FaUser,
  FaPhone,
  FaChartBar,
  FaClock,
  FaVolumeUp,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
} from "react-icons/fa";
import styles from "./CampaignCallTypeViewer.module.css";

const API_BASE = "https://prod.toxboxai.com/api/election-campaign";
const LOG_API_BASE = "https://prod.toxboxai.com/api/election-data/api/election-log";

const CampaignCallTypeViewer = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [callTypes, setCallTypes] = useState([]);
  const [selectedCallType, setSelectedCallType] = useState("");
  const [rawVoters, setRawVoters] = useState([]); // New: store unfiltered voters
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [showPopup, setShowPopup] = useState(false);
  const [callLogData, setCallLogData] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [voterCalls, setVoterCalls] = useState([]);
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [currentVoterId, setCurrentVoterId] = useState(null);

  // ---- FILTER STATES ----
  const [callDurationFilter, setCallDurationFilter] = useState("all"); // all | short | medium | long | custom
  const [durationInput, setDurationInput] = useState(""); // "min-max"

  const getToken = () => localStorage.getItem("token");

  /* ------------------------------------------------------------------ */
  /*                         FETCH CAMPAIGNS                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const fetchCampaigns = async () => {
      const token = getToken();
      if (!token) return console.error("Token missing!");

      try {
        const res = await fetch(`${API_BASE}/logs/call-types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) setCampaigns(data.detailedCampaigns);
      } catch (err) {
        console.error("Error fetching campaigns:", err);
      }
    };
    fetchCampaigns();
  }, []);

  /* ------------------------------------------------------------------ */
  /*                         CAMPAIGN SELECTION                         */
  /* ------------------------------------------------------------------ */
  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign(campaign);
    setCallTypes(campaign.callTypeCompletedListId || []);
    setSelectedCallType(campaign.callTypeCompletedListId?.[0] || "");
    setRawVoters([]);
    setCurrentPage(1);
    resetFilters();
  };

  const resetFilters = () => {
    setCallDurationFilter("all");
    setDurationInput("");
  };

  /* ------------------------------------------------------------------ */
  /*                         FETCH VOTERS (with pagination)            */
  /* ------------------------------------------------------------------ */
  const handleCallTypeSelect = async (type, page = 1) => {
    setSelectedCallType(type);
    setCurrentPage(page);
    const token = getToken();
    if (!token) return console.error("Token missing!");

    try {
      const res = await fetch(`${API_BASE}/voters?callType=${type}&page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success && data.callType === type) {
        const campaignWithVoters = data.campaigns.find(
          (c) => c.campaignId === selectedCampaign.campaignId
        );
        const unfilteredVoters = campaignWithVoters
          ? campaignWithVoters.voters.filter((voter) => {
            const callId = findCallIdForType(voter, type);
            const hasSubType = voter.subType?.includes(type);
            return callId || hasSubType;
          })
          : [];

        setRawVoters(unfilteredVoters);
        setPagination(data.pagination || {});
      } else {
        setRawVoters([]);
      }
    } catch (err) {
      console.error("Error fetching voters:", err);
      setRawVoters([]);
    }
  };

  useEffect(() => {
    if (selectedCampaign && selectedCallType) {
      handleCallTypeSelect(selectedCallType, currentPage);
    } else {
      setRawVoters([]);
    }
  }, [selectedCampaign, selectedCallType, currentPage]);

  /* ------------------------------------------------------------------ */
  /*                         DURATION HELPERS                           */
  /* ------------------------------------------------------------------ */
  const getCallsForType = (voter, targetType) => {
    const calls = [];
    if (!voter.prompts || !Array.isArray(voter.prompts)) return calls;

    if (
      voter.callStatus?.[targetType] === "completed" ||
      voter.subType?.includes(targetType)
    ) {
      for (const prompt of voter.prompts) {
        for (const arrayName of ["joined", "unjoined", "returnResponse", "trashCall"]) {
          if (prompt[arrayName] && Array.isArray(prompt[arrayName])) {
            for (const call of prompt[arrayName]) {
              if (
                (!call.type || call.type === targetType) &&
                call.voterCallStatus === "completed" &&
                call.callID
              ) {
                calls.push({
                  callID: call.callID,
                  callDuration: call.callDuration,
                  callReaction: call.callReaction,
                  callDateTime: call.callDateTime,
                });
              }
            }
          }
        }
      }
    }

    // Sort by date descending (latest first)
    calls.sort((a, b) => Date.parse(b.callDateTime) - Date.parse(a.callDateTime));
    return calls;
  };

  const findCallDurationForType = (voter, targetType) => {
    const calls = getCallsForType(voter, targetType);
    if (!calls.length) return null;
    return calls[0].callDuration;
  };

  const filteredVoters = useMemo(() => {
    if (!rawVoters.length) return [];

    if (callDurationFilter === "all") return rawVoters;

    return rawVoters.filter((v) => {
      const dur = findCallDurationForType(v, selectedCallType);
      if (dur === null) return false;

      switch (callDurationFilter) {
        case "short":
          return dur <= 30;
        case "medium":
          return dur > 30 && dur <= 60;
        case "long":
          return dur > 60;
        case "custom":
          if (!durationInput) return false;
          const [minStr, maxStr] = durationInput.split("-").map((s) => s.trim());
          const min = minStr ? parseFloat(minStr) : -Infinity;
          const max = maxStr ? parseFloat(maxStr) : Infinity;
          if (isNaN(min) || isNaN(max)) return false; // Invalid input
          return dur >= min && dur <= max;
        default:
          return true;
      }
    });
  }, [rawVoters, callDurationFilter, durationInput, selectedCallType]);

  /* ------------------------------------------------------------------ */
  /*                         CALL-ID / REACTION HELPERS                */
  /* ------------------------------------------------------------------ */
  const findCallIdForType = (voter, targetType) => {
    const calls = getCallsForType(voter, targetType);
    if (!calls.length) return null;
    return calls[0].callID;
  };

  const findCallReactionForType = (voter, targetType) => {
    const calls = getCallsForType(voter, targetType);
    if (!calls.length) return null;
    return calls[0].callReaction;
  };

  /* ------------------------------------------------------------------ */
  /*                         POPUP LOGIC                               */
  /* ------------------------------------------------------------------ */
  const handleVoterClick = (voter) => {
    const type = selectedCallType;
    const calls = getCallsForType(voter, type);

    if (!calls.length) {
      alert(`No completed call recording found for this voter with call type "${type}".`);
      return;
    }

    setCurrentVoterId(voter._id);
    setVoterCalls(calls);
    setSelectedCallId(calls[0].callID);
    setShowPopup(true);
  };

  useEffect(() => {
    if (!showPopup || !selectedCallId || !currentVoterId || !selectedCampaign || !selectedCallType) return;

    const fetchLog = async () => {
      const token = getToken();
      if (!token) return console.error("Token missing!");

      try {
        const res = await fetch(
          `${LOG_API_BASE}/${selectedCampaign.campaignId}/${selectedCallType}/${currentVoterId}/${selectedCallId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.success) {
          setCallLogData(data);
          setActiveTab("summary");
        } else {
          alert("Error loading call details.");
        }
      } catch (err) {
        console.error(err);
        alert("Error loading call details.");
      }
    };

    fetchLog();
  }, [selectedCallId, showPopup, currentVoterId, selectedCampaign, selectedCallType]);

  const closePopup = () => {
    setShowPopup(false);
    setCallLogData(null);
    setVoterCalls([]);
    setSelectedCallId(null);
    setCurrentVoterId(null);
  };

  const handleBackToCampaigns = () => {
    setSelectedCampaign(null);
    setSelectedCallType("");
    setRawVoters([]);
    setCurrentPage(1);
    resetFilters();
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      handleCallTypeSelect(selectedCallType, page);
    }
  };

  const getPaginationRange = () => {
    const { currentPage = 1, totalPages = 1 } = pagination;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    rangeWithDots.push(1);
    if (currentPage - delta > 2) rangeWithDots.push("...");
    rangeWithDots.push(...range);
    if (currentPage + delta < totalPages - 1) rangeWithDots.push("...");
    if (totalPages > 1) rangeWithDots.push(totalPages);

    return rangeWithDots;
  };

  const getCallData = () => {
    if (!callLogData?.data) return null;
    const timestamps = Object.keys(callLogData.data);
    if (!timestamps.length) return null;

    const timestamp = timestamps[0];
    const data = callLogData.data[timestamp];

    return {
      callStatus: data.call_statusjson || data.callStatus || {},
      recordingUrl: data.recordingwavUrl || data.recordingUrl,
      transcript: data.transcripttxt || data.transcript,
      analysis: data.sarvam_analysisjson?.result || null,
      timestamp,
    };
  };

  const renderCallLogContent = () => {
    const callData = getCallData();
    if (!callData) return <div className={styles.errorMessage}>No call data available</div>;

    const { callStatus, recordingUrl, transcript, analysis } = callData;

    switch (activeTab) {
      case "summary":
        return (
          <div className={styles.summaryContent}>
            {/* <div className={styles.basicInfo}>
              <div className={styles.infoRow}>
                <span>
                  <FaClock /> Duration: {callStatus.billedDuration || "N/A"}
                </span>
                <span>
                  <FaVolumeUp /> First Speaker: {callStatus.firstSpeaker || "N/A"}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span>End Reason: {callStatus.endReason || "N/A"}</span>
                <span>Call ID: {callStatus.callId || "N/A"}</span>
              </div>
            </div> */}

            <div className={styles.summaryCard}>
              <h4>
                <FaChartBar /> Short Summary
              </h4>
              <p>{callStatus.shortSummary || "No summary available"}</p>
            </div>

            {analysis && (
              <div className={styles.analysisSection}>
                <h4>Analysis</h4>
                <div className={styles.analysisGrid}>
                  <div className={styles.analysisCard}>
                    <h5>Sentiment</h5>
                    <p>{analysis.sentiment_analysis?.overall_sentiment || "N/A"}</p>
                  </div>
                  <div className={styles.analysisCard}>
                    <h5>Emotion</h5>
                    <p>{analysis.emotional_analysis?.primary_emotion || "N/A"}</p>
                  </div>
                  <div className={styles.analysisCard}>
                    <h5>Voting Intention</h5>
                    <p>{analysis.sentiment_analysis?.voting_intention || "N/A"}</p>
                  </div>
                </div>
                {analysis.issues_identified?.local_issues?.length > 0 && (
                  <div className={styles.issuesSection}>
                    <h5>Key Issues</h5>
                    <div className={styles.analysisGrid}>
                      {analysis.issues_identified.local_issues.map((issue, idx) => (
                        <div className={styles.analysisCard} key={idx}>{issue}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {callStatus.summary && (
              <div className={styles.detailedSummary}>
                <h4>Detailed Summary</h4>
                <p>{callStatus.summary}</p>
              </div>
            )}
          </div>
        );

      case "transcript":
        if (!transcript) return <div className={styles.errorMessage}>No transcript available</div>;
        const messages = transcript.split("\n").filter((l) => l.trim());
        return (
          <div className={styles.chatContainer}>
            <div className={styles.chatMessages}>
              {messages.map((line, i) => {
                if (line.startsWith("User:")) {
                  return (
                    <div key={i} className={styles.userMessage}>
                      <span>User</span>
                      <div className={styles.messageBubble}>{line.replace("User: ", "").trim()}</div>
                    </div>
                  );
                }
                if (line.startsWith("Agent:")) {
                  return (
                    <div key={i} className={styles.agentMessage}>
                      <span>Agnet</span>
                      <div className={styles.messageBubble}>{line.replace("Agent: ", "").trim()}</div>
                    </div>
                  );
                }
                return (
                  <div key={i} className={styles.systemMessage}>
                    {line}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "recording":
        if (!recordingUrl) return <div className={styles.errorMessage}>No recording available</div>;
        return (
          <div className={styles.recordingContent}>
            <div className={styles.audioPlayer}>
              <FaFileAudio size={32} className={styles.audioIcon} />
              <audio controls src={recordingUrl} className={styles.audioControl}>
                Your browser does not support the audio element.
              </audio>
            </div>
            <p className={styles.recordingNote}>Recording may expire. Download if needed.</p>
          </div>
        );

      default:
        return null;
    }
  };

  /* ------------------------------------------------------------------ */
  /*                         RENDER UI                                 */
  /* ------------------------------------------------------------------ */
  return (
    <div className={styles.container}>
      {/* ------------------- CAMPAIGN LIST ------------------- */}
      {!selectedCampaign ? (
        <div className={styles.campaignSection}>
          <h2 className={styles.sectionTitle}>Select Campaign</h2>
          <div className={styles.campaignGrid}>
            {campaigns.map((campaign) => (
              <div
                key={campaign.campaignId}
                className={`${styles.campaignCard} ${selectedCampaign?.campaignId === campaign.campaignId ? styles.activeCard : ""
                  }`}
                onClick={() => handleCampaignSelect(campaign)}
              >
                <div className={styles.cardHeader}>
                  <FaUser className={styles.partyIcon} />
                  <div>
                    <h3>{campaign.campaignInfo.partyName}</h3>
                    <p className={styles.constituency}>{campaign.campaignInfo.constituency}</p>
                  </div>
                </div>
                <div className={styles.cardStats}>
                  <div className={styles.statItem}>
                    <span className={styles.statNumber}>{campaign.summary?.totalCalls || 0}</span>
                    <span className={styles.statLabel}>Calls</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statNumber}>{campaign.summary?.totalCallTypes || 0}</span>
                    <span className={styles.statLabel}>Types</span>
                  </div>
                </div>
                <div className={styles.cardFooter}>
                  <p>{campaign.campaignInfo.leaderName}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ------------------- CAMPAIGN DETAIL ------------------- */
        <div className={styles.campaignDetailsSection}>
          <div className={styles.campaignHeader}>
            <button className={styles.backButton} onClick={handleBackToCampaigns}>
              <FaArrowLeft /> Back to Campaigns
            </button>
            <h2 className={styles.sectionTitle}>{selectedCampaign.campaignInfo.partyName}</h2>
            <div className={styles.campaignMeta}>
              <span>{selectedCampaign.campaignInfo.constituency}</span>
              <span>• {selectedCampaign.campaignInfo.leaderName}</span>
            </div>
          </div>

          {/* CALL TYPE CHIPS */}
          <div className={styles.callTypesContainer}>
            <h3 className={styles.subTitle}>Agent Types</h3>
            <div className={styles.callTypesList}>
              {callTypes.map((type) => {
                const stats = selectedCampaign.callTypeStats?.[type];
                return (
                  <div
                    key={type}
                    className={`${styles.callTypeChip} ${selectedCallType === type ? styles.activeCallType : ""}`}
                    onClick={() => handleCallTypeSelect(type)}
                  >
                    <span>{type}</span>
                    <span className={styles.chipCount}>{stats?.voterCount || 0}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* DURATION FILTER */}
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <FaFilter className={styles.filterIcon} />
              <select
                value={callDurationFilter}
                onChange={(e) => setCallDurationFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Durations</option>
                <option value="short">Short (up to 30s)</option>
                <option value="medium">Medium (30-60s)</option>
                <option value="long">Long (over 60s)</option>
                <option value="custom">Custom Range</option>
              </select>

              {callDurationFilter === "custom" && (
                <input
                  type="text"
                  placeholder="e.g. 21-45.42"
                  value={durationInput}
                  onChange={(e) => setDurationInput(e.target.value)}
                  className={styles.customInput}
                />
              )}
            </div>
          </div>

          {/* VOTERS TABLE */}
          <div className={styles.votersSection}>
            <h3 className={styles.subTitle}>Voters</h3>
            {filteredVoters.length > 0 ? (
              <div className={styles.votersTableContainer}>
                <div className={styles.tableHeader}>
                  <div className={styles.headerCell}>Voter</div>
                  <div className={styles.headerCell}>Contact</div>
                  <div className={styles.headerCell}>Duration</div>
                  <div className={styles.headerCell}>Call Sentiment</div>
                </div>

                <div className={styles.votersList}>
                  {filteredVoters.map((voter) => {
                    const calls = getCallsForType(voter, selectedCallType);
                    const latest = calls[0];
                    const duration = latest ? latest.callDuration : null;
                    const durText = duration !== null ? `${duration.toFixed(2)} s` : "N/A";
                    const callReaction = latest ? latest.callReaction : null;

                    return (
                      <div
                        key={voter._id}
                        className={`${styles.voterRow} ${calls.length === 0 ? styles.disabledRow : ""}`}
                        onClick={() => calls.length > 0 && handleVoterClick(voter)}
                      >
                        <div className={styles.voterInfoCell}>
                          <FaUser className={styles.voterIcon} />
                          <div>
                            <div className={styles.voterName}>{voter.voterName}</div>
                            <div className={styles.voterAddress}>{voter.voterAddress}</div>
                          </div>
                        </div>

                        <div className={styles.contactCell}>
                          <FaPhone className={styles.phoneIcon} />
                          <span>{voter.voterContact}</span>
                        </div>

                        <div className={styles.durationCell}>
                          <FaClock className={styles.clockIcon} />
                          <span>{durText}</span>
                        </div>

                        <div className={styles.callReactionCell}>
                          <span
                            className={`${styles.callReaction} ${styles[callReaction] || ""
                              }`}
                          >
                            {callReaction || "N/A"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* PAGINATION */}
                {pagination.totalPages > 1 && (
                  <div className={styles.paginationContainer}>
                    <button
                      className={styles.paginationButton}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <FaChevronLeft />
                    </button>
                    <div className={styles.paginationNumbers}>
                      {getPaginationRange().map((page, idx) => (
                        <button
                          key={idx}
                          className={`${styles.pageNumber} ${page === currentPage ? styles.activePage : ""
                            } ${page === "..." ? styles.pageDots : ""}`}
                          onClick={() => typeof page === "number" && handlePageChange(page)}
                          disabled={page === "..."}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      className={styles.paginationButton}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.totalPages}
                    >
                      <FaChevronRight />
                    </button>
                    <span className={styles.paginationInfo}>
                      Page {currentPage} of {pagination.totalPages}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.noDataContainer}>
                <FaFileAlt className={styles.noDataIcon} />
                <p>No voters found for this call type.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------- CALL LOG POPUP ------------------- */}
      {showPopup && callLogData && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>Call Details - {selectedCallType}</h3>
              <button className={styles.closeButton} onClick={closePopup}>
                <FaTimes />
              </button>
            </div>

            {voterCalls.length > 1 && (
              <div className={styles.callSelector}>
                <label>Select Call:</label>
                <select
                  value={selectedCallId}
                  onChange={(e) => setSelectedCallId(e.target.value)}
                >
                  {voterCalls.map((call) => (
                    <option key={call.callID} value={call.callID}>
                      {call.callDateTime} - {call.callDuration.toFixed(2)}s {call.callReaction ? `(${call.callReaction})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.tabNavigation}>
              <button
                className={`${styles.tabButton} ${activeTab === "summary" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("summary")}
              >
                <FaChartBar /> Summary
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "transcript" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("transcript")}
              >
                <FaFileAlt /> Transcript
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "recording" ? styles.activeTab : ""}`}
                onClick={() => setActiveTab("recording")}
              >
                <FaFileAudio /> Audio
              </button>
            </div>

            <div className={styles.tabContent}>{renderCallLogContent()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignCallTypeViewer;